import { QuickStudyResultsQuestions } from './QuickStudyResultsQuestions';
import { useEffect, useMemo, useRef, useState } from "react";
import type { ThreadAssetView } from "@digital-twins/shared-types/assistant";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "@digital-twins/design-system/icons";
import type {
  ConceptAsset,
  QuickStudyResultsView,
  QuickStudyAuthoredInstrument,
  QuickStudyDraftView,
} from "@digital-twins/shared-types";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import {
  previewSurveyModel,
  previewSharedInstrumentDefinition,
  SurveyPreviewSurface,
} from "@/features/studies/questionnaire/components/QuestionnairePathPreview";
import { quickStudiesApi } from "@/lib/api/quick-study-native";
import { questionRefLabel } from "@digital-twins/shared-types";
import { getTypeOption } from "@/features/studies/questionnaire/hooks/useQuestionnaireWorkspace";
import { QuestionContentEditor } from "@/features/studies/questionnaire/components/QuestionnaireQuestionCard";
import { assetMentionTablesFor } from "@/features/studies/questionnaire/assets/conceptAssets";
import { assetIdsShownInSharedView } from "@/features/studies/questionnaire/utils/sharedViewAssets";
import { assetMediaUrl } from "@/features/studies/questionnaire/assets/assetMedia";

function Preview({
  draft,
  threadAssets,
  onRendered,
}: {
  draft: QuickStudyDraftView;
  threadAssets: ReadonlyArray<ThreadAssetView>;
  onRendered: () => void;
}) {
  const { t } = useTranslation("app");
  const [model, setModel] = useState<ReturnType<
    typeof previewSurveyModel
  > | null>(null);
  const rendered = useRef(onRendered);
  rendered.current = onRendered;
  const imageUrlFor = useMemo(() => {
    // These are existing authorized browser views, keyed by the immutable file
    // identity the compiler stores as mediaId. Never expose storage locators.
    const urls = new Map(threadAssets.filter(asset => asset.ref.kind === "image")
      .map(asset => [asset.ref.id, asset.ref.publicUrl]));
    return (mediaId: string) => urls.get(mediaId) || undefined;
  }, [threadAssets]);
  useEffect(() => {
    if (!draft.compiledSurveyJson) {
      setModel(null);
      return;
    }
    // SurveyJS owns resources. Creation and disposal belong to the same effect
    // so StrictMode's cleanup/replay cannot reuse a disposed memoized instance.
    const presented = previewSharedInstrumentDefinition(
      draft.compiledSurveyJson,
      draft.instrument?.questions ?? [],
      t("questionnaire.pathPreview.stimulusOwnOrder"),
      imageUrlFor,
    );
    const survey = previewSurveyModel(
      presented.definition,
      presented.stimulusOrder,
    );
    survey.mode = "display";
    survey.showNavigationButtons = false;
    survey.questionsOnPageMode = "singlePage";
    survey.showInvisibleElements = true;
    setModel(survey);
    return () => survey.dispose();
  }, [draft.compiledSurveyJson, draft.instrument, imageUrlFor, t]);
  useEffect(() => {
    if (model) rendered.current();
  }, [model]);
  return model ? <SurveyPreviewSurface model={model} /> : null;
}

/** Reads the same saved figures as Studies; never calculates replacements in the client. */
export function ThreadQuickStudyPanel({
  threadToken,
  threadAssets,
  draft,
  onClose,
  onViewed,
  onSaved,
}: {
  threadToken: string;
  threadAssets: ReadonlyArray<ThreadAssetView>;
  draft: QuickStudyDraftView;
  onClose: () => void;
  onViewed?: (stage: "questions" | "results") => void;
  onSaved?: (next: QuickStudyDraftView) => void;
}) {
  const { t } = useTranslation("app");
  const [evidence, setEvidence] = useState<QuickStudyResultsView | null>(
    null,
  );
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [questionsRendered, setQuestionsRendered] = useState(false);
  useEffect(
    () => setQuestionsRendered(false),
    [draft.draftToken, draft.revision],
  );
  const [editing, setEditing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [edited, setEdited] = useState<QuickStudyAuthoredInstrument | null>(
    draft.instrument,
  );
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const saveOperation = useRef<string | null>(null);
  const saveInFlight = useRef(false);
  const savedAssets = draft.testedAssets?.assets ?? [];
  const concepts = savedAssets.filter((asset) => asset.type === "concept");
  const mentionTables = assetMentionTablesFor(savedAssets, savedAssets);
  const run = draft.activeRun;
  // Option thumbnails: the saved image's own hosted URL, else the authorized
  // thread view of the same file (keyed by the immutable media id).
  const resolveMediaUrl = useMemo(() => {
    const urls = new Map(
      threadAssets
        .filter((asset) => asset.ref.kind === "image")
        .map((asset) => [asset.ref.id, asset.ref.publicUrl]),
    );
    return (media: ConceptAsset["media"][number]) =>
      assetMediaUrl(media) || urls.get(media.fileId ?? media.id) || urls.get(media.id) || undefined;
  }, [threadAssets]);
  useEffect(() => {
    setEdited(draft.instrument);
    setEditing(false);
    setPreviewing(false);
    setSaveFailed(false);
    saveOperation.current = null;
  }, [draft.draftToken, draft.revision]);
  async function save() {
    if (!edited || !draft.canEdit || saveInFlight.current) return;
    saveInFlight.current = true;
    setSaving(true);
    setSaveFailed(false);
    saveOperation.current ??= crypto.randomUUID();
    try {
      const next = await quickStudiesApi.save(
        threadToken,
        draft.draftToken,
        draft.revision,
        saveOperation.current,
        edited,
      );
      setEditing(false);
      setEdited(next.instrument);
      saveOperation.current = null;
      onSaved?.(next);
    } catch {
      setSaveFailed(true);
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  }

  // Acknowledgement follows mounting the saved author content, never merely
  // opening the drawer. Compilation is still required for question readiness.
  useEffect(() => {
    if (
      !editing &&
      !previewing &&
      draft.instrument?.questions.length &&
      draft.compiledSurveyJson
    ) {
      setQuestionsRendered(true);
    }
  }, [draft.instrument, draft.compiledSurveyJson, editing, previewing]);

  const viewed = useRef(onViewed);
  viewed.current = onViewed;
  const acknowledged = useRef("");
  useEffect(() => {
    const stage =
      run?.status === "completed" && evidence
        ? "results"
        : !run &&
            draft.status === "ready" &&
            questionsRendered &&
            draft.compiledSurveyJson
          ? "questions"
          : null;
    const key = stage
      ? `${draft.draftToken}:${draft.revision}:${stage}:${run?.runToken ?? ""}`
      : "";
    if (!stage || acknowledged.current === key) return;
    acknowledged.current = key;
    viewed.current?.(stage);
  }, [
    draft.draftToken,
    draft.revision,
    draft.status,
    draft.compiledSurveyJson,
    questionsRendered,
    run?.runToken,
    run?.status,
    evidence,
  ]);

  useEffect(() => {
    let current = true;
    setEvidence(null);
    setFailed(false);
    if (!run || run.status !== "completed") return;
    void quickStudiesApi
      .findings(threadToken, draft.draftToken, run.runToken)
      .then((value) => {
        if (value.runToken !== run.runToken)
          throw new Error("Unexpected Quick Study evidence identity");
        if (current) setEvidence(value);
      })
      .catch(() => {
        if (current) setFailed(true);
      });
    return () => {
      current = false;
    };
  }, [threadToken, draft.draftToken, run?.runToken, run?.status, attempt]);
  return (
    <section
      className="flex h-full min-h-0 flex-col bg-background text-foreground"
      data-testid="thread-native-quick-study-panel"
    >
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border px-4 py-3">
        <Text
          size="sm"
          className="order-first min-w-0 basis-full break-words md:basis-0 md:flex-1 md:truncate"
        >
          {draft.title}
        </Text>
        {run?.status !== "completed" && !editing && draft.compiledSurveyJson ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPreviewing((value) => !value)}
          >
            {t(
              previewing
                ? "threads.quickStudy.reviewQuestions"
                : "questionnaire.pathPreview.button",
            )}
          </Button>
        ) : null}
        {draft.canEdit && !run && !editing && !previewing ? (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            {t("threads.quickStudy.edit")}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("threads.quickStudy.close")}
          onClick={onClose}
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </header>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        {run?.status !== "completed" ? (
          !previewing ? (
            <div className="space-y-5">
              {saveFailed ? (
                <Text size="sm" role="alert" className="text-destructive">
                  {t("threads.quickStudy.saveFailed")}
                </Text>
              ) : null}
              {(editing ? edited : draft.instrument)?.questions.map(
                (question, index) => (
                  <div
                    key={question.id}
                    className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 [&_input:disabled]:cursor-default [&_input:disabled]:opacity-100 [&_textarea:disabled]:cursor-default [&_textarea:disabled]:opacity-100"
                    data-testid="quick-author-question"
                  >
                    <div className="flex items-center gap-2">
                      <Text
                        as="span"
                        size="xs"
                        className="rounded-full bg-primary/10 px-2 py-1 text-primary"
                      >
                        {questionRefLabel(index)}
                      </Text>
                      <Text as="span" size="xs" color="muted">
                        {getTypeOption(question).badgeLabel}
                      </Text>
                    </div>
                    <QuestionContentEditor
                      question={question}
                      index={index}
                      language={(editing ? edited : draft.instrument)?.language}
                      conceptAssets={concepts}
                      mentionTables={mentionTables}
                      disabled={!editing || saving}
                      resolveMediaUrl={resolveMediaUrl}
                      exposedAssetIds={
                        draft.compiledSurveyJson
                          ? assetIdsShownInSharedView(
                              draft.compiledSurveyJson as Record<string, unknown>,
                              question,
                            )
                          : undefined
                      }
                      onUpdate={(update) => {
                        if (!editing || saving) return;
                        saveOperation.current = null;
                        setEdited((current) =>
                          current
                            ? {
                                ...current,
                                questions: current.questions.map((item) =>
                                  item.id === question.id ? update(item) : item,
                                ),
                              }
                            : current,
                        );
                      }}
                    />
                  </div>
                ),
              )}
              {editing ? (
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    disabled={saving}
                    onClick={() => {
                      setEdited(draft.instrument);
                      setEditing(false);
                      setSaveFailed(false);
                      saveOperation.current = null;
                    }}
                  >
                    {t("cancel", { ns: "common" })}
                  </Button>
                  <Button disabled={saving} onClick={() => void save()}>
                    {saving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    {t("save", { ns: "common" })}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : (
            <Preview
              draft={draft}
              threadAssets={threadAssets}
              onRendered={() => setQuestionsRendered(true)}
            />
          )
        ) : failed ? (
          <div role="alert" className="space-y-3">
            <Text size="sm">{t("threads.quickStudy.resultsFailed")}</Text>
            <Button
              variant="outline"
              onClick={() => setAttempt((value) => value + 1)}
            >
              {t("threads.quickStudy.retry")}
            </Button>
          </div>
        ) : !evidence ? (
          <div role="status" className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <Text size="sm" color="muted">
              {t("threads.quickStudy.loadingResults")}
            </Text>
          </div>
        ) : (
          <QuickStudyResultsQuestions evidence={evidence} questions={draft.instrument?.questions ?? []} onRetry={() => setAttempt(value => value + 1)} />
        )}
      </div>
    </section>
  );
}
